import { useState, useEffect } from "react";
import { useVendorMode } from "@/contexts/VendorModeContext";
import { Menu, X, ArrowLeftRight, Truck, User } from "lucide-react";

/**
 * ModeSwitcher — hamburger menu overlay for switching between vendor and user modes.
 * Shown in both vendor mode (for vendors) and user mode (for verified vendors only).
 * Non-vendor users never see this.
 *
 * v1.0.0 Gate 3 — In user mode, only rendered on the Profile tab. No vendor
 * entry point on homepage or any other user-facing screen.
 */
export default function ModeSwitcher() {
  const [open, setOpen] = useState(false);
  const [pathname, setPathname] = useState(() => (typeof window !== "undefined" ? window.location.pathname : "/"));
  const { vendorMode, switchMode, isVerified } = useVendorMode();

  // Track route changes (BrowserRouter history API updates pathname without navigation events)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", update);
    const interval = setInterval(update, 400);
    return () => {
      window.removeEventListener("popstate", update);
      clearInterval(interval);
    };
  }, []);

  if (!isVerified) return null;
  // v1.0.0 — in user mode, only expose vendor switcher on Profile tab
  if (!vendorMode && pathname !== "/profile") return null;

  const handleSwitch = () => {
    switchMode();
    setOpen(false);
  };

  return (
    <>
      {/* Hamburger button */}
      <button
        onClick={() => setOpen(true)}
        className={`fixed top-3 left-14 z-50 w-10 h-10 rounded-full flex items-center justify-center transition-all touch-target ${
          vendorMode
            ? "bg-[#2C2C2E] text-white/70 hover:text-[#F59E0B]"
            : "bg-white/90 text-slate-600 hover:text-sky-500 shadow-sm border border-slate-200"
        }`}
        data-testid="mode-switcher-hamburger"
      >
        <Menu className="w-4 h-4" />
      </button>

      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-start" data-testid="mode-switcher-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className={`relative mt-14 ml-3 rounded-xl shadow-2xl p-3 w-60 ${
            vendorMode ? "bg-[#2C2C2E] border border-white/10" : "bg-white border border-slate-200"
          }`}>
            <button onClick={() => setOpen(false)}
              className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center ${
                vendorMode ? "text-white/40 hover:text-white" : "text-slate-400 hover:text-slate-600"
              }`}
            >
              <X className="w-3.5 h-3.5" />
            </button>

            <div className="space-y-2 mt-1">
              <button
                onClick={handleSwitch}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                  vendorMode
                    ? "bg-sky-500/10 hover:bg-sky-500/20 text-sky-400"
                    : "bg-[#1C1C1E]/5 hover:bg-[#1C1C1E]/10 text-[#1C1C1E]"
                }`}
                data-testid="switch-mode-button"
              >
                {vendorMode ? <User className="w-5 h-5" /> : <Truck className="w-5 h-5" />}
                <span className="text-sm font-semibold">
                  {vendorMode ? "Switch to User Mode" : "Switch to Vendor Mode"}
                </span>
              </button>

              <button
                onClick={() => setOpen(false)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                  vendorMode
                    ? "bg-white/5 hover:bg-white/10 text-white/60"
                    : "bg-slate-50 hover:bg-slate-100 text-slate-500"
                }`}
                data-testid="stay-mode-button"
              >
                <ArrowLeftRight className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {vendorMode ? "Stay in Vendor Mode" : "Stay in User Mode"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
